import { SyncStrategy } from "@fusion-advantage/formlib"
import { XFrontendRequired } from "../../lib/XFrontend"

// since a complete sync process is heavy and can involve up to dozens of
// separate http requests, it's preferable to do some kind of partial sync
// most of the time

export enum SyncType {
	// a complete sync including "offline" supporting data tables
	// use for app start in the morning, and manually-requested syncs
	// user must have connectivity or an error will be displayed
	Complete = 'Complete',
	// a complete sync upon app load
	// but defers dialog to avoid screen flicker
	Boot = 'Boot',
	// a complete sync excluding supporting data tables (which change rarely)
	// use for interday operations, manage encounter actions, etc.
	// user must have connectivity or an error will be displayed
	Dashboard = 'Dashboard',
	// a boot sync excluding supporting data tables (which change rarely)
	// if on mobile and if an active session already exists
	MobileReboot = 'MobileReboot',
	// a dashboard sync if online, deferred for later background sync if not
	DashboardDeferrable = 'DashboardDeferrable',
	// a fully backgrounded dashboard sync
	// if user does not have connectivity, no error will be displayed
	DashboardBackground = 'DashboardBackground',
	// a boot sync for the /preview branch route: supporting data tables and
	// the previewed branch, but none of the work items
	Preview = 'Preview',
	// logout
	Logout = 'Logout',
	// initiateLogin is issued a frontend update requirement by the server;
	// display a sync dialog with download status or notice
	FrontendRequired = 'FrontendRequired',
}

export const syncShouldDefer = (type: SyncType, strategy: SyncStrategy): boolean => {
	switch(type) {
		case SyncType.DashboardDeferrable:
			if(strategy === SyncStrategy.Mobile) {
				return true
			}
			break
	}
	return false
}

export const syncShouldShowModalImmediate = (type: SyncType): boolean => {
	switch(type) {
		case SyncType.Complete:
		case SyncType.Dashboard:
		case SyncType.FrontendRequired:
			return true
		case SyncType.DashboardDeferrable:
			// note: this will be deferred if strategy is mobile,
			// so this case is for when the user is online only
			return true
	}
	return false
}

export const syncShouldShowModalDelayed = (type: SyncType): boolean => {
	switch(type) {
		case SyncType.Boot:
		case SyncType.MobileReboot:
		case SyncType.Preview:
			return true
	}
	return false
}

export const syncShouldDownloadOfflineTables = (type: SyncType): boolean => {
	switch(type) {
		case SyncType.Boot:
		case SyncType.Complete:
		case SyncType.Preview:
			return true
	}
	return false
}

export const syncShouldDownloadWorkItems = (type: SyncType): boolean => {
	switch(type) {
		case SyncType.Preview:
			return false
	}
	return true
}

// allow urgent software updates on most sync types
export const syncShouldInstallImmediateBundleUpdates = (type: SyncType): boolean => {
	switch(type) {
		case SyncType.DashboardBackground:
			return false
	}
	return true
} 

export const syncShouldTickLocation = (type: SyncType): boolean => {
	switch(type) {
		case SyncType.Preview:
			// the preview form is never saved, so has no use for a location
			return false
		case SyncType.DashboardBackground:
			// we don't need to refresh location every time we sync items
			// in the background; the locationMw handles that for us on its
			// own timer
			return false
	}
	return true
}

export const syncShouldSuppressErrorAlert = (type: SyncType): boolean => {
	switch(type) {
		case SyncType.DashboardBackground:
			return true
	}
	return false
}

export type SyncConfig = {
	type: (
		SyncType.Complete|
		SyncType.Boot|
		SyncType.Dashboard|
		SyncType.MobileReboot|
		SyncType.DashboardBackground|
		SyncType.DashboardDeferrable|
		SyncType.Logout
	)
	adhocEncounterGuid?: never
	adhocBranchGuid?: never
	downloadPatchedRenderers?: never
	skipLocation?: never
} | {
	type: (
		SyncType.Boot|
		SyncType.Dashboard
	)
	adhocEncounterGuid?: string
	adhocBranchGuid?: never
	downloadPatchedRenderers?: boolean
	// don't start the location watch (or its permission modal) on sync end
	skipLocation?: boolean
} | {
	type: SyncType.Preview
	adhocBranchGuid: string
	adhocEncounterGuid?: never
	downloadPatchedRenderers?: never
	skipLocation?: never
} | {
	type: (
		SyncType.FrontendRequired
	)
	adhocEncounterGuid?: never
	adhocBranchGuid?: never
	downloadPatchedRenderers?: never
	skipLocation?: never
	frontendRequired: XFrontendRequired
}
